import type { SelectHTMLAttributes } from 'react'
import { useId } from 'react'

import { Icon } from './Icon'

/**
 * The picker fields: province and city on screen 16, and the three date parts
 * on screen 11. Both are the same shape — value right, chevron left — at two
 * sizes: the 60-high radius-16 field, and a 52-high radius-14 compact box.
 *
 * A native `<select>` carries the whole thing: it gives the platform picker,
 * keyboard access and form semantics for free, which a custom sheet would have
 * to rebuild. The chevron is decorative; the native arrow is hidden.
 */
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  error?: string | null
  size?: 'field' | 'compact'
  placeholder?: string
  options: readonly { value: string; label: string }[]
}

export function Select({
  label,
  error,
  size = 'field',
  placeholder,
  options,
  className = '',
  id,
  ...rest
}: SelectProps) {
  const generatedId = useId()
  const selectId = id ?? generatedId
  const compact = size === 'compact'

  return (
    <div className="flex w-full flex-col gap-[10px]">
      {label ? (
        <label htmlFor={selectId} className="text-12 leading-[19px] font-medium text-ink-80">
          {label}
        </label>
      ) : null}

      <div
        className={[
          'relative flex items-center border bg-surface-5 transition-colors duration-(--sx-dur)',
          compact ? 'h-[52px] rounded-14 px-3' : 'h-field rounded-16 ps-2 pe-4',
          error ? 'border-danger' : 'border-line-16 focus-within:border-primary-glow',
          className,
        ].join(' ')}
      >
        <select
          id={selectId}
          className={[
            'peer w-full appearance-none bg-transparent text-ink outline-none',
            compact ? 'text-13 text-center' : 'text-15 text-right',
            'ps-0 pe-5',
          ].join(' ')}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-bg text-ink">
              {option.label}
            </option>
          ))}
        </select>
        <Icon
          name="chevron-down"
          size={12}
          className="pointer-events-none absolute end-3 text-ink-50"
        />
      </div>

      {error ? (
        <p role="alert" className="text-12 leading-[19px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
